import '../../modules/mode-components';

import * as angular from 'angular';

import { AccountFacade } from '@mode/accounts/contract';
import { ReportRunResourceHelpers, SpaceHelpers, SpaceResource, formSubmit } from '@mode/legacy/data-access-model-js';
import { ReportCloneFacade } from '@mode/reports/domain';
import { FeatureFlag, Logger } from '@mode/shared/contract-common';
import { getCsrfParam, getCsrfToken, getInitialFlagValue } from '@mode/shared/util-js';
import { combineLatestWith, filter, first, take, takeUntil } from 'rxjs/operators';
import template from './clone-modal.html';
import cloneLink = ReportRunResourceHelpers.cloneLink;
import hasCloneLink = ReportRunResourceHelpers.hasCloneLink;
import { NotebookSecretFacade } from '@mode/notebooks/contract';
import { DataViewFacade } from '@mode/switch-views/contract';
import { Subject } from 'rxjs';

enum CloneMethod {
  Direct = 'Direct',
  DirectNoContext = 'DirectNoContext',
  Interactive = 'Interactive',
}

const cloneModalDirective = function (
  Modal,
  accountFacade: AccountFacade,
  dataViewFacade: DataViewFacade,
  $log: Logger,
  $window,
  reportCloneFacade: ReportCloneFacade,
  notebookSecretFacade: NotebookSecretFacade
) {
  return {
    restrict: 'A',
    scope: {
      account: '<',
      currentContext: '@',
      clonePath: '@',
      report: '<',
      reportRun: '<',
      reportSpace: '<',
      loggedOut: '<', // user is unauthenticated
      owner: '<',
    },
    link: function ($scope, elem, attrs) {
      const doc = $window.document;
      const csrfParam = getCsrfParam(doc);
      const csrfToken = getCsrfToken(doc);
      const enableSecureCredentials = getInitialFlagValue(FeatureFlag.RegisterSecureCredentialsNotebooks);
      const enablePublishExportedDatasets = getInitialFlagValue(FeatureFlag.NotebookPublishExportedDatasets);
      const destroy$ = new Subject<void>();

      const getClonePath = () => {
        const reportRunResource = $scope.reportRun && $scope.reportRun._resource;

        return hasCloneLink(reportRunResource) ? cloneLink(reportRunResource) : $scope.clonePath;
      };

      const initiateClone = (ownerId: string, target?: string) => {
        reportCloneFacade.dispatch(
          ReportCloneFacade.Actions.initReportClone({
            ownerId,
            reportId: $scope.report.token,
            runId: $scope.reportRun.token,
            reportWorkspace: $scope.report._resource.account_username,
          })
        );
      };

      accountFacade.loadUserOrgs();
      if (enableSecureCredentials) {
        notebookSecretFacade.notebookToken$
          .pipe(
            take(1),
            filter((session) => !!session)
          )
          .subscribe(() => notebookSecretFacade.loadSecrets());
      }
      elem.on('click.context', (event) => {
        if (!attrs.evented) {
          event.preventDefault();
          event.stopPropagation();
        }
        if (attrs.disabled) {
          return;
        }
        // when secure credentials or publish export dataset is enabled
        if (enableSecureCredentials || enablePublishExportedDatasets) {
          accountFacade.userOrgs$
            .pipe(
              first(),
              combineLatestWith(
                notebookSecretFacade.isSecretsExist$.pipe(take(1)),
                dataViewFacade.enhancedDatasetResults$.pipe(take(1))
              ),
              takeUntil(destroy$)
            )
            .subscribe(([orgs, isSecretsExist, enhancedDatasetResults]) => {
              $scope.orgs = orgs;
              const isPublishedDataset = enhancedDatasetResults.some((dataset) => !!dataset.thoughtspot);
              // if publish export dataset or secret exist in report
              if (
                (enableSecureCredentials && isSecretsExist) ||
                (enablePublishExportedDatasets && isPublishedDataset)
              ) {
                dataViewFacade.openDuplicateDialog(() => clone(getCloneMethod()), isSecretsExist, isPublishedDataset);
              } else {
                clone(getCloneMethod());
              }
            });
        } else {
          accountFacade.userOrgs$.pipe(first()).subscribe((orgs) => {
            $scope.orgs = orgs;
            clone(getCloneMethod());
          });
        }

        function clone(method: CloneMethod) {
          switch (method) {
            case CloneMethod.Direct:
              cloneDirect();
              break;
            case CloneMethod.DirectNoContext:
              cloneDirectWithNoContext();
              break;
            case CloneMethod.Interactive:
              cloneInteractive();
              break;
            default:
              $log.error('Clone Error: No method selected');
              break;
          }
        }

        function getCloneMethod(): CloneMethod | null {
          let method: CloneMethod | null = null;
          const space: SpaceResource = $scope.reportSpace && $scope.reportSpace._resource;
          const userBelongsToOwner =
            $scope.orgs && $scope.owner && $scope.orgs.some((o) => o.username === $scope.owner.username);

          if (!space) {
            $log.error('Clone Error: report space resource not available');
          }

          if (userBelongsToOwner && !SpaceHelpers.isCommunitySpace(space)) {
            method = CloneMethod.Direct;
          } else if ($scope.loggedOut) {
            method = CloneMethod.DirectNoContext;
          } else if ($scope.orgs && $scope.account) {
            method = CloneMethod.Interactive;
          } else {
            $log.error('Clone Error: account or account orgs not provided');
          }

          return method;
        }

        /**
         *
         * Clone directly into the same org context of the report
         */
        function cloneDirect() {
          initiateClone($scope.owner.id.toString(), '_blank');
        }

        /**
         *
         * Submit form with out an owner value to enter sign-in flow
         */
        function cloneDirectWithNoContext() {
          formSubmit(getClonePath(), {
            target: '_blank',
            fields: {
              authenticity_token: csrfToken,
            },
          });
        }

        /**
         *
         * Spawn a modal for the user to select the destination
         * of this clone
         */
        function cloneInteractive() {
          Modal.open(
            {
              csrfParam,
              csrfToken,
              account: $scope.account,
              organizations: $scope.orgs,
              context: attrs.currentContext,
              currentOrg: $scope.owner.username,
              initiateClone,
            },
            {
              template: template,
            }
          );
        }
      });

      // cleanup handlers
      $scope.$on('$destroy', () => {
        elem.off('click.context');
        destroy$.next();
        destroy$.complete();
      });
    },
  };
};

cloneModalDirective.$inject = [
  'ModalService',
  'AccountFacade',
  'DataViewFacade',
  '$log',
  '$window',
  'ReportCloneFacade',
  'NotebookSecretFacade',
];

angular.module('mode.components').directive('cloneModal', cloneModalDirective);
